import { formatUnits } from "@ethersproject/units";
import { WeiPerEther } from "@ethersproject/constants";
import { BigNumber } from "@ethersproject/bignumber";
import { TVL_THRESHOLD } from "utils/constants";
import {
  StakerInfo,
  Pool,
  PoolPositions,
  AnteTest,
  PoolDB,
  PoolSideInfoDB,
  PoolSideInfo,
  PoolComputedFields,
  PoolVersionType,
} from "types";
import {
  formatAnteNumber,
  getPoolToken,
  newPoolDateThreshold,
  toLocaleNumber,
} from "utils/utils";
import { BadgeType } from "types/BadgeType";
import colors from "style/colors";
import { Chip } from "@material-ui/core";
import ObjectID from "bson-objectid";

/**
 * Trust Score = (total staked in non-failed tests) /
 *    (AVL in non-failed tests + 5% of pre-failure AVL in tests that failed within the past 3 months)
 */
export const calculateTrustScore = (
  tvl: BigNumber,
  totalStaked: BigNumber,
  preFailureTvl: BigNumber = BigNumber.from(0)
): number => {
  if (!tvl || tvl.eq(0)) return 0;

  return Number(
    formatUnits(
      totalStaked.mul(10000).div(
        tvl.add(
          // 5% of pre-failure TVL
          preFailureTvl.div(20)
        )
      ),
      2
    )
  );
};

const formatPoolInfo = (poolSideInfo: PoolSideInfoDB): PoolSideInfo => {
  return {
    numUsers: Number(poolSideInfo?.numUsers),
    totalAmount: formatAnteNumber(poolSideInfo?.totalAmount),
    decayMultiplier: formatAnteNumber(poolSideInfo?.decayMultiplier),
  };
};

export const formatPools = (pools: PoolDB[]): Pool[] => {
  // Filter pools with unsupported format
  return pools.map(formatPool).filter((p) => !!p);
};

export const getCalculatedFields = (pool: Pool): PoolComputedFields => {
  const tvlBN = pool.stakingInfo?.totalAmount.bn
    .add(pool.challengerInfo?.totalAmount.bn)
    .add(pool.totalPendingWithdraw?.bn);
  const token = getPoolToken(pool);
  const stakedAndPendingWithdrawBN = pool.stakingInfo?.totalAmount.bn.add(
    pool.totalPendingWithdraw?.bn
  );

  const trustScore = pool.pendingFailure
    ? 0
    : calculateTrustScore(tvlBN, stakedAndPendingWithdrawBN);
  const isTvlGteThreshold = tvlBN.gte(WeiPerEther.mul(TVL_THRESHOLD));

  const { stakedBalance, challengedBalance, pendingWithdrawAmount } = pool;

  let poolPosition = undefined;
  if (stakedBalance && challengedBalance && pendingWithdrawAmount) {
    if (stakedBalance.bn.gt(0) || pendingWithdrawAmount.bn.gt(0)) {
      poolPosition = PoolPositions.Staked;
    } else if (challengedBalance.bn.gt(0)) {
      poolPosition = PoolPositions.Challenged;
    } else {
      poolPosition = PoolPositions.None;
    }
  }

  return {
    tvl: formatAnteNumber(tvlBN, token?.decimals),
    trustScore,
    isTvlGteThreshold,
    stakedAndPendingWithdraw: formatAnteNumber(
      stakedAndPendingWithdrawBN,
      pool.token.decimals
    ),
    poolPosition,
  };
};

export const formatPool = (pool: PoolDB): Pool => {
  const stakingInfo = formatPoolInfo(pool.stakingInfo);
  const challengerInfo = formatPoolInfo(pool.challengerInfo);
  const totalPendingWithdraw = formatAnteNumber(pool.pendingWithdrawAmount);
  const token = getPoolToken(pool);
  if (!token) return null;

  const { pendingWithdrawAmount, ...inheritedFields } = pool;

  const formattedPool = {
    ...inheritedFields,
    stakingInfo,
    challengerInfo,
    totalPendingWithdraw,
    version: pool.version || "v0.5",
    minSupporterStake: formatAnteNumber(
      pool.minSupporterStake,
      token?.decimals
    ),
    minChallengerStake: formatAnteNumber(
      pool.minChallengerStake,
      token?.decimals
    ),
    token,
    numTimesVerified: Number(pool.numTimesVerified),
    lastVerifiedBlock: Number(pool.lastVerifiedBlock),
    timestamp: new ObjectID(pool._id).getTimestamp().getTime(),
  };

  return {
    ...formattedPool,
    ...getCalculatedFields(formattedPool),
  };
};

export const updateStakerAmounts = (
  stakers: StakerInfo[],
  updatedStakerBalances: Record<string, BigNumber>
) => {
  if (
    updatedStakerBalances === undefined ||
    Object.entries(updatedStakerBalances).length === 0
  ) {
    return stakers;
  }

  return stakers.map((staker: StakerInfo) => {
    let newAmount = updatedStakerBalances[staker.address]
      ? BigNumber.from(updatedStakerBalances[staker.address]).toString()
      : null;
    return {
      ...staker,
      amount: newAmount ?? staker.amount,
    };
  });
};

export type PoolBadgeMap = {
  [key in BadgeType]?: boolean;
};

export const getBadges = (test: AnteTest): PoolBadgeMap => {
  const { hasProtocolStaked, timestamp } = test;

  return {
    selfStaked: hasProtocolStaked,
    new: timestamp > newPoolDateThreshold(),
  };
};

export const calculateEstimatedPayout = (pool: Pool): string => {
  if (
    pool.stakedAndPendingWithdraw === undefined ||
    pool.challengedBalance === undefined ||
    pool.verifierBounty === undefined
  ) {
    return "-";
  }

  const totalChallenged =
    pool.version < "v0.6"
      ? pool.challengerInfo.totalAmount
      : pool.totalChallengerEligibleBalance;

  if (totalChallenged.bn.eq(0)) {
    return "-";
  }

  return toLocaleNumber(
    Number(
      formatUnits(
        pool.stakedAndPendingWithdraw.bn
          .sub(pool.verifierBounty.bn)
          .mul(pool.challengedBalance.bn)
          .div(totalChallenged.bn),
        pool.token.decimals
      )
    )
  );
};

export const renderPoolVersionBadge = (version: PoolVersionType) => {
  if (version >= "v0.6") return null;
  return (
    <Chip
      size="small"
      label="LEGACY"
      color="primary"
      style={{ backgroundColor: colors.warningYellow, fontWeight: 600 }}
    />
  );
};
